import Input from "components/ui/Input";
import TextArea from "components/ui/TextArea";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import useAuth from "hooks/useAuth";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useProfilingMasterForm } from "hooks/useProfilingMasterForm";
import Modal from "components/ui/modals";

const FormSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-6 bg-gray-200 rounded w-1/3 mb-6"></div>
    <div className="grid grid-cols-2 gap-4">
      {[1, 2, 3, 4].map((n) => (
        <div key={n}>
          <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-full"></div>
        </div>
      ))}
    </div>
    <div className="h-16 bg-gray-200 rounded w-full mt-2"></div>
    {[1, 2, 3].map((s) => (
      <div key={s} className="mt-4">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
        {[1, 2, 3].map((r) => (
          <div key={r} className="flex gap-3 mb-2">
            <div className="flex-1 h-8 bg-gray-200 rounded"></div>
            <div className="w-24 h-8 bg-gray-200 rounded"></div>
            <div className="w-24 h-8 bg-gray-200 rounded"></div>
            <div className="w-14 h-8 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const profileTypeOptions = [
  { value: "V", label: "Vendor" },
  { value: "C", label: "Customer" },
];

const commissionConditionOptions = [
  { value: "", label: "Select" },
  { value: "SB", label: "Sale Bill Amount" },
  { value: "SBT", label: "SB before taxes" },
];

const DiscountSection = ({
  title,
  discountType,
  rows,
  allDiscountsForType,
  isLoading,
  getAvailableDiscounts,
  addRow,
  removeRow,
  updateRowDiscount,
  updateRowValue,
  moveRowUp,
  moveRowDown,
}) => {
  const noMoreDiscounts = rows.length >= allDiscountsForType.length;

  return (
    <div>
      <div className="flex items-center justify-between mb-2 border-b border-gray-100 pb-1">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}
        </h3>
        <button
          type="button"
          onClick={() => addRow(discountType)}
          disabled={noMoreDiscounts}
          className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Add
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((n) => (
            <div key={n} className="flex gap-3 animate-pulse">
              <div className="flex-1 h-8 bg-gray-200 rounded"></div>
              <div className="w-24 h-8 bg-gray-200 rounded"></div>
              <div className="w-24 h-8 bg-gray-200 rounded"></div>
              <div className="w-20 h-8 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-1">
          No discounts added. Click "Add" to select discounts.
        </p>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-1 px-0.5">
            <span className="flex-1 text-xs text-gray-400 min-w-0">Discount</span>
            <span className="w-24 text-xs text-gray-400 text-center">% Value</span>
            <span className="w-24 text-xs text-gray-400 text-center">Rs. Value</span>
            <span className="w-20 text-xs text-gray-400 text-center">Actions</span>
          </div>

          <div className="space-y-1.5">
            {rows.map((row, index) => {
              const available = getAvailableDiscounts(discountType, row._rowId);
              const selectedDiscount = allDiscountsForType.find(
                (d) => d.id === row.discount_id
              );
              const selectValue = selectedDiscount
                ? { value: selectedDiscount.id, label: selectedDiscount.name }
                : null;
              const selectOptions = available.map((d) => ({
                value: d.id,
                label: d.name,
              }));

              return (
                <div key={row._rowId} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <Select
                      value={selectValue}
                      onChange={(opt) =>
                        updateRowDiscount(discountType, row._rowId, opt?.value || null)
                      }
                      options={selectOptions}
                      placeholder="Select discount"
                      isClearable={false}
                      isSearchable={true}
                    />
                  </div>

                  <div className="w-24">
                    <Input
                      type="number"
                      value={row.percentage_value}
                      onChange={(e) =>
                        updateRowValue(
                          discountType,
                          row._rowId,
                          "percentage_value",
                          e.target.value
                        )
                      }
                      min="0"
                      max="100"
                      step="0.001"
                      placeholder="0"
                      disabled={!row.discount_id}
                    />
                  </div>

                  <div className="w-24">
                    <Input
                      type="number"
                      value={row.amount_value}
                      onChange={(e) =>
                        updateRowValue(
                          discountType,
                          row._rowId,
                          "amount_value",
                          e.target.value
                        )
                      }
                      min="0"
                      step="0.001"
                      placeholder="0"
                      disabled={!row.discount_id}
                    />
                  </div>

                  <div className="w-20 flex gap-0.5 justify-center flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => moveRowUp(discountType, row._rowId)}
                      disabled={index === 0}
                      className="w-6 h-6 bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed rounded flex items-center justify-center transition-colors"
                      title="Move up"
                    >
                      <ChevronUpIcon className="h-3 w-3 text-gray-600" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveRowDown(discountType, row._rowId)}
                      disabled={index === rows.length - 1}
                      className="w-6 h-6 bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed rounded flex items-center justify-center transition-colors"
                      title="Move down"
                    >
                      <ChevronDownIcon className="h-3 w-3 text-gray-600" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeRow(discountType, row._rowId)}
                      className="w-6 h-6 bg-red-50 hover:bg-red-100 rounded flex items-center justify-center transition-colors"
                      title="Remove"
                    >
                      <TrashIcon className="h-3 w-3 text-red-500" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default function SetProfilingMaster({ id, onClose, open }) {
  const { agttp } = useAuth();

  const {
    profileType,
    selectedParty,
    profilingDetails,
    isLoadingProfiling,
    isLoadingDiscounts,
    isCreating,
    isUpdating,
    annualDiscounts,
    billDiscounts,
    afterTaxDiscounts,
    annualRows,
    billRows,
    afterTaxRows,
    handleInputChange,
    handleProfileTypeChange,
    handlePartyChange,
    handleCommissionConditionChange,
    getAvailableDiscounts,
    addRow,
    removeRow,
    updateRowDiscount,
    updateRowValue,
    moveRowUp,
    moveRowDown,
    handleSubmit,
  } = useProfilingMasterForm(id, onClose);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit();
  };

  const isPending = isCreating || isUpdating;

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      maxWidth="2xl"
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Edit" : "Add"} Profiling Master
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-700"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-profiling-form"
            disabled={isPending || !profilingDetails.ps_Id}
            className="rounded-md bg-gray-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      {id && isLoadingProfiling ? (
        <FormSkeleton />
      ) : (
      <form id="set-profiling-form" onSubmit={handleFormSubmit}>
      <div className="space-y-4">
        {/* Profile Type + Party */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select
            value={profileType}
            onChange={handleProfileTypeChange}
            options={profileTypeOptions}
            label="Type"
            placeholder="Select type"
            isRequired={true}
            isSearchable={false}
          />

          {profileType?.value === "C" ? (
            <SelectCustomers
              value={selectedParty}
              onChange={handlePartyChange}
              label="Customer"
              placeholder="Select customer"
              isRequired={true}
              initialValue={id ? selectedParty : null}
            />
          ) : (
            <SelectVendors
              value={selectedParty}
              onChange={handlePartyChange}
              label="Vendor"
              placeholder="Select vendor"
              isRequired={true}
              initialValue={id ? selectedParty : null}
            />
          )}
        </div>

        {/* Date + numeric fields */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Input
            type="date"
            name="date"
            label="Date"
            value={profilingDetails.date}
            onChange={handleInputChange}
            isRequired={true}
          />
          <Input
            type="number"
            name="noofdaysCD"
            label="Cash Disc. Days"
            value={profilingDetails.noofdaysCD}
            onChange={handleInputChange}
            min="0"
          />
          <Input
            type="number"
            name="noofdayspay"
            label="Payment Days"
            value={profilingDetails.noofdayspay}
            onChange={handleInputChange}
            min="0"
          />
          <Input
            type="number"
            name="intprcnt"
            label="Interest Rate %"
            value={profilingDetails.intprcnt}
            onChange={handleInputChange}
            min="0"
            step="0.01"
          />
        </div>

        {/* Agent Commission */}
        {agttp === 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input
              type="number"
              name="agentcom"
              label="Commission %"
              value={profilingDetails.agentcom}
              onChange={handleInputChange}
              min="0"
              step="0.01"
            />
            <div className="col-span-2">
              <Select
                value={commissionConditionOptions.find(
                  (o) => o.value === profilingDetails.agencommcond
                )}
                onChange={handleCommissionConditionChange}
                options={commissionConditionOptions}
                label="Commission On"
                placeholder="Select"
                isSearchable={false}
              />
            </div>
          </div>
        )}

        {/* Terms */}
        <TextArea
          name="terms"
          label="Terms"
          value={profilingDetails.terms}
          onChange={handleInputChange}
          maxLength="1000"
          rows={3}
          placeholder="Terms and conditions..."
        />

        {/* Discount Sections */}
        <div className="space-y-5 pt-1">
          <DiscountSection
            title="Annual Discounts"
            discountType="A"
            rows={annualRows}
            allDiscountsForType={annualDiscounts}
            isLoading={isLoadingDiscounts}
            getAvailableDiscounts={getAvailableDiscounts}
            addRow={addRow}
            removeRow={removeRow}
            updateRowDiscount={updateRowDiscount}
            updateRowValue={updateRowValue}
            moveRowUp={moveRowUp}
            moveRowDown={moveRowDown}
          />
          <DiscountSection
            title="Before-Tax Bill Discounts"
            discountType="B"
            rows={billRows}
            allDiscountsForType={billDiscounts}
            isLoading={isLoadingDiscounts}
            getAvailableDiscounts={getAvailableDiscounts}
            addRow={addRow}
            removeRow={removeRow}
            updateRowDiscount={updateRowDiscount}
            updateRowValue={updateRowValue}
            moveRowUp={moveRowUp}
            moveRowDown={moveRowDown}
          />
          <DiscountSection
            title="After-Tax Bill Discounts"
            discountType="C"
            rows={afterTaxRows}
            allDiscountsForType={afterTaxDiscounts}
            isLoading={isLoadingDiscounts}
            getAvailableDiscounts={getAvailableDiscounts}
            addRow={addRow}
            removeRow={removeRow}
            updateRowDiscount={updateRowDiscount}
            updateRowValue={updateRowValue}
            moveRowUp={moveRowUp}
            moveRowDown={moveRowDown}
          />
        </div>
      </div>
      </form>
      )}
    </Modal>
  );
}
